import { Search, X } from 'lucide-react';
import { playClickSound } from '@/utils/soundUtils';
import {
  wfFormControlClass,
  wfSearchFormPad,
  wfSearchToolbarPad,
  wfToolbarControlClass,
} from '@/styles/wireframeForm';
import { cx } from './cx';

export interface SearchPillProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'> {
  containerClassName?: string;
  /**
   * `form` — wireframe fields (FAQ, modals, deposit/withdraw panels): rounded rect.
   * `toolbar` — filter row pill (Games): compact pill beside chips.
   */
  variant?: 'toolbar' | 'form';
  /** Toolbar only; defaults to true when variant is toolbar. */
  compact?: boolean;
  /** Toolbar compact shape — pill (default) or rounded box. */
  shape?: 'pill' | 'box';
  clearable?: boolean;
  onClear?: () => void;
  suffix?: React.ReactNode;
}

/** Cosmos search field — two surfaces shared app-wide via wireframeForm tokens. */
export default function SearchPill({
  containerClassName,
  className,
  variant = 'toolbar',
  compact,
  shape = 'pill',
  clearable = false,
  onClear,
  suffix,
  value,
  onChange,
  ...rest
}: SearchPillProps) {
  const hasValue = value != null && String(value).length > 0;
  const isForm = variant === 'form';
  const isToolbar = !isForm;
  const isCompact = compact ?? isToolbar;

  const handleClear = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    playClickSound();
    if (onClear) {
      onClear();
      return;
    }
    onChange?.({ target: { value: '' } } as React.ChangeEvent<HTMLInputElement>);
  };

  const iconSize = isForm ? 'calc(14px * var(--proto-scale))' : 'calc(12px * var(--proto-scale))';

  return (
    <div
      className={cx(
        'flex items-center min-w-0',
        isForm && cx('gap-2 w-full', wfFormControlClass),
        isToolbar &&
          isCompact &&
          cx(
            'gap-1.5 shrink-0',
            wfToolbarControlClass,
            shape === 'box' && '!rounded-[calc(7px*var(--proto-scale))]'
          ),
        containerClassName
      )}
      style={isForm ? wfSearchFormPad : wfSearchToolbarPad}
    >
      <Search
        className="text-ice/70 shrink-0"
        style={{ width: iconSize, height: iconSize }}
        aria-hidden
      />
      <input
        type="search"
        value={value}
        onChange={onChange}
        className={cx(
          'w-full min-w-0 bg-transparent font-ui text-ink placeholder:text-caption-muted',
          'focus:outline-none',
          className
        )}
        {...rest}
      />
      {clearable && hasValue ? (
        <button
          type="button"
          onClick={handleClear}
          aria-label="Clear search"
          className="shrink-0 inline-flex items-center justify-center text-ice/70 hover:text-ink transition-colors"
        >
          <X style={{ width: iconSize, height: iconSize }} />
        </button>
      ) : null}
      {suffix}
    </div>
  );
}
